import {
  deleteAssignment,
  insertAssignmentRemainder,
  setAssignmentEnd,
  setAssignmentStart,
} from '../../services/assignments';
import { confirmAsk, confirmDelete, notify } from '../../store/feedback';
import { addDays, dateFromIso, isoStr } from '../../utils/dates';
import { fmtDate, uid } from '../../utils/format';

const MAJ_KO = 'Erreur lors de la mise à jour : ';

/* Retrait d'une journée d'une affectation : suppression si elle ne dure qu'un jour, sinon
   raccourcissement par le début ou la fin, ou découpage en deux affectations autour du jour. */
export default function planningDeleteDayHandlers({ update }) {
  const patch = (id, champs) =>
    update((s) => ({
      ...s,
      affectations: s.affectations.map((a) => (a.id === id ? { ...a, ...champs } : a)),
    }));

  // Coupe l'affectation autour du jour : la première partie finit la veille, la suite est recréée.
  async function splitAround(aff, prevIso, nextIso) {
    const origFin = aff.fin;
    const { error: errUpd } = await setAssignmentEnd(aff, prevIso);
    if (errUpd) return notify(MAJ_KO + errUpd.message);
    const newId = 'af_' + uid();
    const { error: errIns } = await insertAssignmentRemainder(aff, newId, nextIso, origFin);
    if (errIns)
      return notify(
        "Erreur : la journée a été retirée mais la suite de l'affectation n'a pas pu être recréée (" +
          errIns.message +
          '). Rechargez la page avant de continuer.',
      );
    const suite = {
      id: newId,
      devId: aff.devId,
      briqueId: aff.briqueId,
      debut: nextIso,
      fin: origFin,
      heuresParJour: aff.heuresParJour,
    };
    update((s) => ({
      ...s,
      affectations: [
        ...s.affectations.map((a) => (a.id === aff.id ? { ...a, fin: prevIso } : a)),
        suite,
      ],
    }));
  }

  // Message de confirmation : on dit ce que la suppression va réellement produire.
  function dayDeletionMessage(aff, dayIso) {
    const day = fmtDate(dayIso);
    if (aff.debut === dayIso && aff.fin === dayIso)
      return `L'affectation du ${day} sera supprimée.`;
    if (aff.debut === dayIso || aff.fin === dayIso)
      return `Le ${day} sera retiré de l'affectation (${fmtDate(aff.debut)} → ${fmtDate(aff.fin)}).`;
    return `Le ${day} sera retiré : l'affectation sera coupée en deux (${fmtDate(aff.debut)} → veille, puis lendemain → ${fmtDate(aff.fin)}).`;
  }

  // Renvoie false si l'utilisateur annule, pour garder la fenêtre ouverte.
  async function deleteAssignmentDay(aff, dayIso) {
    const ok = await confirmAsk(dayDeletionMessage(aff, dayIso), {
      title: 'Supprimer la journée',
      confirmLabel: 'Supprimer',
      danger: true,
    });
    if (!ok) return false;
    const prevIso = isoStr(addDays(dateFromIso(dayIso), -1));
    const nextIso = isoStr(addDays(dateFromIso(dayIso), 1));
    if (aff.debut === dayIso && aff.fin === dayIso) {
      const { error } = await deleteAssignment(aff);
      if (error) return notify('Erreur lors de la suppression : ' + error.message);
      update((s) => ({ ...s, affectations: s.affectations.filter((a) => a.id !== aff.id) }));
    } else if (aff.debut === dayIso) {
      const { error } = await setAssignmentStart(aff, nextIso);
      if (error) return notify(MAJ_KO + error.message);
      patch(aff.id, { debut: nextIso });
    } else if (aff.fin === dayIso) {
      const { error } = await setAssignmentEnd(aff, prevIso);
      if (error) return notify(MAJ_KO + error.message);
      patch(aff.id, { fin: prevIso });
    } else await splitAround(aff, prevIso, nextIso);
    return true;
  }

  async function deleteWholeAssignment(aff) {
    const ok = await confirmDelete(
      `Supprimer l'affectation du ${fmtDate(aff.debut)} au ${fmtDate(aff.fin)} ?`,
    );
    if (!ok) return false;
    const { error } = await deleteAssignment(aff);
    if (error) {
      notify('Erreur lors de la suppression : ' + error.message);
      return false;
    }
    update((s) => ({ ...s, affectations: s.affectations.filter((a) => a.id !== aff.id) }));
    return true;
  }

  return { deleteAssignmentDay, deleteWholeAssignment };
}
